<template>
  <div class="as-employee-inspection-requests-table">
    <!-- Search and Filter Bar -->
    <div class="table-top-actions">
      <div class="search-inspection-item w-100">
        <SearchBar
          @onSearch="handleSearch"
          type="button"
          :label="localAppliedFilters.length > 0 ? 'More Filters' : 'Filters'"
          :length="localAppliedFilters.length"
          :chipText="filterLabels"
          ghostText="Search Responsibility"
          :add="false"
          @onClick="showFilterDialog = true"
          :valueSearch="localSearchText"
          @removeFilter="handleRemoveFilter"
          @clearSelection="handleClearSelection"
          :counterSection="false"
          :flexibleLayout="true"
          class="custom-search-bar"
        />
      </div>
    </div>

    <!-- Table Content -->
    <NoRecordFound
      v-if="filteredData.length === 0"
      :should-show-add-action="false"
      :description="localSearchText ? 'No results found for your search' : 'You have no pending inspection requests'"
      class="no-record-found"
      :noBorder="true"
      :showActiveCounter="false"
      :showSpecificMessage="localSearchText ? 'Try adjusting your search criteria' : 'Once you make an inspection request, it will show here until completed or canceled'"
      :showClearAction="isFiltered"
      clearLabel="Clear All"
      @onClear="handleClearSelection"
    />
    <div v-else class="table-container">
      <DxDataGrid
        id="as-employee-inspection-requests-datagrid"
        :data-source="paginatedData"
        :column-auto-width="true"
        :word-wrap-enabled="true"
        :show-row-lines="true"
        :show-column-lines="false"
        :show-borders="false"
        :element-attr="{ class: 'as-employee-inspection-requests-table-grid' }"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        column-resizing-mode="widget"
        :allow-sorting="false"
        no-data-text=""
        :loading-enabled="false"
        :loadPanel="{ enabled: false }"
      >
        <DxStateStoring
          :enabled="true"
          type="localStorage"
          storage-key="as-employee-inspection-requests-table-state"
        />
        <DxRowDragging
          :allow-reordering="false"
          :on-reorder="() => {}"
          :show-drag-icons="false"
        />

        <DxColumn
          data-field="levelRequested"
          caption="For level / Requested on"
          cell-template="levelRequested"
          :min-width="550"
        />
        <DxColumn
          data-field="inspectionStatus"
          caption="Requested from"
          cell-template="inspectionStatus"
          :min-width="280"
        />
        <DxColumn
          data-field="status"
          caption="Status"
          cell-template="statusBadge"
          :width="130"
        />
        <DxColumn
          data-field="actions"
          caption=""
          cell-template="actions"
          :min-width="150"
        />
        <DxColumn
          data-field="chatter"
          caption=""
          cell-template="chatter"
          :width="60"
        />

        <template #levelRequested="{ data: cellData }">
          <div class="responsibility-dropdown-wrapper">
            <div class="level-requested-on-column">
              <div class="level-checkbox-wrapper">
                <div class="level-checkbox">
                  <MinimalisticReadinessLevel :level="cellData.data.level" />
                </div>
                <div class="chevron-divider mt-1">
                  <Icon name="chevron_divider_icon" />
                </div>
                <div class="level-checkbox">
                  <MinimalisticReadinessLevel :level="cellData.data.targetLevel" />
                </div>
              </div>
              <div v-if="cellData.data.requestedOn" class="requested-on-text">
                On {{ formatRequestedOn(cellData.data.requestedOn) }}
              </div>
            </div>
            <ResponsibilityAssignmentDropDown
              :responsibilityCode="cellData.data.responsibilityCode"
              :responsibilityName="cellData.data.responsibilityName"
              :responsibilityImportance="cellData.data.responsibilityImportance"
              :roleCode="cellData.data.roleCode"
              :roleName="cellData.data.roleName"
              :roleImportance="cellData.data.roleImportance"
              :positionCode="cellData.data.positionCode"
              :positionName="cellData.data.positionName"
              :positionImportance="cellData.data.positionImportance"
              :positionCommitment="cellData.data.positionCommitment"
              :show_codes="true"
              resMenuWidth="auto"
            />
            <div v-if="cellData.data.isExpedited" class="expedited-badge">
              Expedited
            </div>
          </div>
        </template>

        <template #inspectionStatus="{ data: cellData }">
          <div class="status-info d-flex align-center">
            <!-- Pending Scenario -->
            <template v-if="cellData.data.status === 'pending'">
              <div v-if="cellData.data.has_inspectors" class="available-inspectors d-flex align-center">
                <span class="available-label mr-2">Available Inspectors:</span>
                <PeopleAvatarsButton
                  :avatars="cellData.data.inspectors"
                  size="small"
                  :maxVisible="3"
                  :isShowTooltip="true"
                />
              </div>
              <div v-else class="no-inspectors d-flex align-center">
                <div class="mr-2">
                  <Icon name="no_inspectors_icon" />
                </div>
                <span class="no-inspectors-text">No inspectors</span>
              </div>
            </template>

            <!-- Accepted/Done/Canceled Scenario -->
            <template v-else>
              <div class="inspector-info d-flex align-center">
                <PeopleAvatarsButton
                  :avatars="[{img: cellData.data.inspector?.img, firstName: cellData.data.inspector?.firstName, lastName: cellData.data.inspector?.lastName}]"
                  size="large"
                />
                <div class="inspector-text">
                  <div class="main-text">{{ getStatusActionText(cellData.data.status) }} by</div>
                  <div class="inspector-name">{{ cellData.data.inspector?.firstName }} {{ cellData.data.inspector?.lastName }}</div>
                </div>
              </div>
            </template>
          </div>
        </template>

        <template #statusBadge="{ data: cellData }">
          <div class="status-badge-container">
            <InspectionRequestStatusBadge :status="cellData.data.status" />
          </div>
        </template>

        <template #actions="{ data: cellData }">
          <div class="action-btn-container d-flex align-center">
            <!-- Prepare Button (ER-11510: destination/behavior not yet confirmed with
                 design — emits onPrepare and lets the parent decide where it goes).
                 Gated behind idea-MDP-176 alongside the unified all-statuses view. -->
            <StandardButton
              v-if="unifiedInspectionRequestsEnabled && ['pending', 'accepted'].includes(cellData.data.status)"
              type="magenta"
              variant="primary"
              size="small"
              label="Prepare"
              aria-label="Prepare inspection request"
              @click="handlePrepare(cellData.data)"
              class="prepare-btn mr-2"
            />

            <!-- Cancel Button (Rule 12 — no reason required, single-click confirm) -->
            <StandardButton
              v-if="['pending', 'accepted'].includes(cellData.data.status)"
              type="red"
              variant="outline"
              size="small"
              label="Cancel"
              aria-label="Cancel inspection request"
              @click="handleCancel(cellData.data)"
              class="cancel-btn"
            />
            
            <!-- Progression for Passed -->
            <div v-else-if="cellData.data.status === 'passed' || cellData.data.status === 'done'" class="pass-fail-indicator d-flex align-center">
              <div class="level-checkbox ml-2">
                <MinimalisticReadinessLevel :level="cellData.data.level" />
              </div>
              <div class="chevron-double">
                <Icon name="chevron_double_icon" />
              </div>
              <div class="level-checkbox">
                <MinimalisticReadinessLevel :level="cellData.data.targetLevel" />
              </div>
            </div>

            <!-- Reason Text (for Canceled/Abandoned/Failed) -->
            <div v-if="['canceled', 'failed', 'abandoned'].includes(cellData.data.status) && cellData.data.reason" class="reason-text truncate-text">
              {{ cellData.data.reason }}
            </div>
          </div>
        </template>

        <template #chatter="{ data: cellData }">
          <div class="chatter-wrapper">
            <ChatterIcon class="cursor-pointer" @onClick="$emit('onChatterClick', cellData.data)" />
          </div>
        </template>
      </DxDataGrid>
    </div>

    <!-- Pagination -->
    <div class="table-footer mt-10" v-if="showPagination">
      <ExPagination
        :pagination="internalPagination"
        @onRequest="handlePaginationRequest"
      />
    </div>

    <!-- Filter Modal -->
    <SearchModal
      :dialog="showFilterDialog"
      @back="showFilterDialog = false"
      @checkedItemsChanged="handleFilterChange"
      :filterGroups="filterGroups"
      :statusOptions="statusOptions"
      :filterType="filterType"
      :defaultSelected="localAppliedFilters"
      :accountType="accountType"
      :clearSelection="clearSelection"
    />
  </div>
</template>

<script setup>
import { ref, watch, computed, getCurrentInstance, onMounted } from "vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import ResponsibilityAssignmentDropDown from "@components/ResponsibilityAssignmentDropDown/ResponsibilityAssignmentDropDown.vue";
import PeopleAvatarsButton from "@components/PeopleAvatarsButton/PeopleAvatarsButton.vue";
import InspectionRequestStatusBadge from "@components/InspectionRequestStatusBadge/InspectionRequestStatusBadge.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import MinimalisticReadinessLevel from "@components/MinimalisticReadinessLevel/MinimalisticReadinessLevel.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import { DxDataGrid, DxColumn, DxRowDragging, DxStateStoring } from "devextreme-vue/data-grid";
import Icon from "@components/icons/Icon.vue";
import { LD_FLAG_IDEA_MDP_176 } from "@components/constants/launchDarklyFlagKeys";

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "onSearch",
  "onFilterChange",
  "onRemoveFilter",
  "onClearFilters",
  "onPaginationRequest",
  "onCancel",
  "onPrepare",
  "onChatterClick",
]);

// ER-11510 / idea-MDP-176 — rollout gate for the unified table (every status
// shown by default + the "Prepare" row action). Off (or unresolved) preserves
// the legacy Pending/Accepted-only view with Cancel as the only action.
const { proxy } = getCurrentInstance() || {};
const unifiedInspectionRequestsEnabled = ref(false);
onMounted(async () => {
  try {
    unifiedInspectionRequestsEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_176, false)) === true;
  } catch (error) {
    unifiedInspectionRequestsEnabled.value = false;
  }
  // Flag resolves after the legacy default below is set — flip to the unified
  // (all-statuses) view once it comes back on, unless the employee already
  // changed the filter themselves.
  if (unifiedInspectionRequestsEnabled.value && !hasUserChangedFilters.value) {
    localAppliedFilters.value = [];
  }
});

const showFilterDialog = ref(false);
const localSearchText = ref("");
const hasUserChangedFilters = ref(false);
// Legacy default: Pending/Accepted only. Flipped to "show every status" in
// onMounted once idea-MDP-176 resolves on (ER-11510).
const localAppliedFilters = ref(["Pending", "Accepted"]);
const filterGroups = ref([]);
const statusOptions = ref([
  "Pending",
  "Accepted",
  "Completed",
  "Failed",
  "Declined",
  "Canceled",
]);
// Filter labels that cover more than one stored status value (stored values do not change).
const FILTER_STORED_STATUSES = {
  completed: ["completed", "passed", "done"],
  declined: ["declined", "abandoned"],
};
const filterType = ref("Inspection Requests");
const accountType = ref("");
const clearSelection = ref("");

const internalPagination = ref({
  total: 0,
  page: 1,
  perPage: 10,
});

const filterLabels = computed(() => {
  const valueToLabelMap = {};
  (filterGroups.value || []).forEach((group) => {
    (group.items || []).forEach((item) => {
      valueToLabelMap[item.value] = item.label;
    });
  });
  return (localAppliedFilters.value || []).map((value) => valueToLabelMap[value] || value);
});

// ✅ Local filtering for immediate UI feedback (same as other files)
const filteredData = computed(() => {
  let result = [...props.data];

  // Search filter (by user name OR responsibility name)
  if (localSearchText.value) {
    const query = localSearchText.value.toLowerCase();
    result = result.filter(item => {
      const inspectorName = `${item.inspector?.firstName || ''} ${item.inspector?.lastName || ''}`.toLowerCase();
      const respName = (item.responsibilityName || '').toLowerCase();
      
      // Also search in available inspectors for pending requests
      const hasAvailableInspectorMatch = item.status === 'pending' && Array.isArray(item.inspectors) && 
        item.inspectors.some(ins => `${ins.firstName || ''} ${ins.lastName || ''}`.toLowerCase().includes(query));

      return inspectorName.includes(query) || respName.includes(query) || hasAvailableInspectorMatch;
    });
  }

  // Status filter
  if (localAppliedFilters.value.length > 0) {
    result = result.filter(item => {
      // Filter values are the option labels; compare case-insensitively against the stored status
      const itemStatus = item.status?.toLowerCase();
      return localAppliedFilters.value.some(filter => {
        const key = filter.toLowerCase();
        return (FILTER_STORED_STATUSES[key] || [key]).includes(itemStatus);
      });
    });
  }

  return result;
});

// ✅ Local pagination (same as other files)
const paginatedData = computed(() => {
  const perPage = Number(internalPagination.value.perPage) || 10;
  const start = (internalPagination.value.page - 1) * perPage;
  const end = start + perPage;
  return filteredData.value.slice(start, end);
});

const showPagination = computed(() => {
  const perPage = Number(internalPagination.value.perPage) || 10;
  return filteredData.value.length > perPage;
});

watch(() => filteredData.value.length, (newLength) => {
  internalPagination.value.total = newLength;
}, { immediate: true });

const isFiltered = computed(() => {
  return localSearchText.value.length > 0 || localAppliedFilters.value.length > 0;
});

const handleSearch = (val) => {
  localSearchText.value = val;
  internalPagination.value.page = 1;
  emit("onSearch", val);
};

const handleRemoveFilter = (removedLabel) => {
  hasUserChangedFilters.value = true;
  const valueToLabelMap = {};
  (filterGroups.value || []).forEach((group) => {
    (group.items || []).forEach((item) => {
      valueToLabelMap[item.value] = item.label;
    });
  });
  
  const removedValue = Object.keys(valueToLabelMap).find(key => valueToLabelMap[key] === removedLabel) || removedLabel;
  
  localAppliedFilters.value = localAppliedFilters.value.filter(f => f !== removedValue);
  clearSelection.value = removedValue;
  internalPagination.value.page = 1;
  emit("onRemoveFilter", removedValue);
  emit("onFilterChange", [...localAppliedFilters.value]);

  // Reset clearSelection after a short delay so the watcher in SearchModal can be triggered again for the same value
  setTimeout(() => {
    clearSelection.value = "";
  }, 100);
};

const handleClearSelection = () => {
  hasUserChangedFilters.value = true;
  localSearchText.value = "";
  localAppliedFilters.value = [];
  internalPagination.value.page = 1;
  emit("onSearch", "");
  emit("onClearFilters");
  emit("onFilterChange", []);
};

const handleFilterChange = (filters) => {
  hasUserChangedFilters.value = true;
  const newArray = (filters || [])
    .map((item) => {
      if (item?.value !== null) {
        if (typeof item === "string") return item;
        if (typeof item === "object" && Object.prototype.hasOwnProperty.call(item, "value")) {
          return item.value;
        }
      }
      return null;
    })
    .filter(Boolean);

  localAppliedFilters.value = newArray;
  showFilterDialog.value = false;
  internalPagination.value.page = 1;
  emit("onFilterChange", newArray);
};

const handlePaginationRequest = (data) => {
  internalPagination.value.page = data.page;
  emit("onPaginationRequest", data);
};

const getStatusActionText = (status) => {
  switch (status?.toLowerCase()) {
    case 'accepted': return 'Accepted';
    case 'canceled': return 'Canceled';
    case 'passed':
    case 'done': return 'Completed';
    case 'failed': return 'Failed';
    case 'abandoned':
    case 'declined': return 'Declined';
    default: return status;
  }
};

// ER-11510: "For level / Requested on" column — matches Figma copy shape
// ("On Aug 19, 2026"). Only rendered when the row actually carries a date.
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];
const formatRequestedOn = (date) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  // UTC getters: date-only ISO strings ("2026-08-19") parse as UTC midnight,
  // so local-timezone getters could roll the day back — use UTC consistently.
  return `${MONTH_NAMES[parsed.getUTCMonth()]} ${parsed.getUTCDate()}, ${parsed.getUTCFullYear()}`;
};

// Rule 12 — Cancel never requires a reason; it just delegates to the parent.
const handleCancel = (item) => {
  emit("onCancel", item);
};

// ER-11510: destination/behavior not yet confirmed with design — the table
// only emits intent, the consuming page decides where "Prepare" goes.
const handlePrepare = (item) => {
  emit("onPrepare", item);
};

defineExpose({
  handleCancel,
  handlePrepare,
  formatRequestedOn,
  getStatusActionText,
  unifiedInspectionRequestsEnabled,
});
</script>

<style scoped>
.as-employee-inspection-requests-table {
  width: 100%;
  background: #fff;
  padding: 24px 16px;
  font-family: 'Inter', sans-serif;
}

.table-top-actions {
  margin-bottom: 24px;
  padding: 0px 14px;
}

.custom-search-bar :deep(.filter-button) {
  padding-left: 0 !important;
}

.custom-search-bar :deep(.search-button) {
  padding-right: 0 !important;
}

.custom-search-bar :deep(.search-button .ex_text_input) {
  width: 70%;
}

.table-container {
  width: 100%;
}

/* DxDataGrid Custom Styles */
.as-employee-inspection-requests-table :deep(.dx-datagrid) {
  background: #fff;
  width: 100% !important;
  max-width: 100% !important;
}

.as-employee-inspection-requests-table :deep(.dx-datagrid-headers) {
  background-color: #F9FAF7;
  color: #667085;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  border-bottom: none;
}

.as-employee-inspection-requests-table :deep(.dx-datagrid-headers .dx-header-row > td) {
  padding: 12px 20px !important;
}

.as-employee-inspection-requests-table :deep(.dx-datagrid-rowsview .dx-row > td) {
  padding: 12px 20px !important;
  border-bottom: 1px solid #eaecf0 !important;
  vertical-align: middle;
}

.as-employee-inspection-requests-table :deep(.dx-datagrid-content .dx-datagrid-table .dx-row > td) {
  height: auto !important;
}

.level-checkbox {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.responsibility-dropdown-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.level-checkbox-wrapper {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.level-requested-on-column {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}

.responsibility-dropdown-wrapper :deep(.rad__trigger-btn) {
  border: 1px solid #D0D5DD !important;
  background: #FFFFFF !important;
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05) !important;
  border-radius: 8px !important;
  height: 44px !important;
  flex: 1;
  min-width: 0;
  padding: 0 12px !important;
}

.responsibility-dropdown-wrapper :deep(.rad__resp-name) {
  font-weight: 500 !important;
  color: #101828 !important;
  font-size: 14px !important;
}

.responsibility-dropdown-wrapper :deep(.rad__resp-text span) {
  font-size: 14px !important;
}

.expedited-badge {
  padding: 2px 10px;
  background: #FDF2FA;
  border: 1px solid #FCCEEE;
  border-radius: 6px;
  color: #C11574;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.available-inspectors {
  display: flex;
  align-items: center;
  white-space: nowrap;
  flex-wrap: nowrap;
}

.available-inspectors :deep(.people-button) {
  left: 0 !important;
}

.available-label {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #101828;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-info {
  display: flex;
  align-items: center;
}

.no-inspectors-text {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  position: relative;
  top: -2px;
}

.warning-icon-outer svg {
  width: 20px;
  height: 20px;
}

.inspector-text {
  display: flex;
  flex-direction: column;
}

.main-text {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.inspector-name {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.reason-text {
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #667085;
  max-width: 140px;
}

.truncate-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}

.cursor-pointer {
  cursor: pointer;
}

.chevron-divider svg path {
  stroke-width: 2.5;
}

.chevron-double {
  display: flex;
  align-items: center;
}

.pass-fail-indicator {
  margin-left: 8px;
}

.table-footer {
  width: 100%;
}

.cancel-btn {
  /* margin-left: 14px; removed to match grid spacing */
}

.prepare-btn {
  white-space: nowrap;
}

.requested-on-text {
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  line-height: 14.52px;
  color: #667085;
  white-space: nowrap;
}
</style>
